import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';

import clsx from 'clsx';
import { Form, Formik } from 'formik';

import { Input, MultiSelect, SingleSelect } from '@/common';
import { Button, Textarea } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { PAGE_SIZE } from '@/constants';
import { getEnabledStatusFilterOptions } from '@/helpers';
import { createUpdateDeviceSchema } from '@/pages/Devices/DeviceValidation';
import { BuildingsIcon } from '@/resources/icons/sidebar';
import { useGetAllOrganizations } from '@/services';
import { useGetAllPlayerReleases } from '@/services/playerRelease.service';
import {
  CreateEditDeviceFormProp,
  CreateEditOrganization,
  PlayerRelease,
  SelectOptionProps
} from '@/types';

const CreateEditDeviceForm = ({
  showOrganizationSelect,
  selectedOrganizations,
  setSelectedOrganizations,
  isSubmitting,
  handleSubmit,
  isEditPage,
  backendError,
  initialValues
}: CreateEditDeviceFormProp) => {
  const [t] = useTranslation('common');

  const [searchOrganization, setSearchOrganization] = useState<string>('');

  const {
    data: allOrganizations,
    isLoading: isLoadingOrganizations,
    fetchNextPage,
    hasNextPage
  } = useGetAllOrganizations(PAGE_SIZE, searchOrganization);

  const {
    data: allPlayerReleases,
    isLoading: isLoadingPlayerReleases,
    fetchNextPage: fetchNextPageReleases,
    hasNextPage: hasNextPageReleases
  } = useGetAllPlayerReleases(PAGE_SIZE);

  const getOrganizationOptions = () => {
    return allOrganizations?.pages.flatMap((page) =>
      page.data?.items.map((org: CreateEditOrganization) => ({
        value: org.id,
        label: org.name
      }))
    ) as SelectOptionProps[];
  };

  const getPlayerReleaseOptions = () => {
    return allPlayerReleases?.pages.flatMap((page) =>
      page.data?.items.map((playerRelease: PlayerRelease) => ({
        value: playerRelease.id,
        label: playerRelease.version
      }))
    ) as SelectOptionProps[];
  };

  const handleOrganizationChange = (
    value: SelectOptionProps[],
    setFieldValue: (field: string, value: SelectOptionProps[] | number[]) => void
  ) => {
    setSelectedOrganizations(value);
    setFieldValue(
      'organizationIds',
      Array.isArray(value) ? value.map((m) => Number(m?.value)).filter((id) => !isNaN(id)) : []
    );
  };

  const handleOrganizationRemove = (
    org: SelectOptionProps,
    setFieldValue: (field: string, value: SelectOptionProps[] | number[]) => void
  ) => {
    if ((selectedOrganizations as SelectOptionProps[])?.includes(org)) {
      const updated = selectedOrganizations.filter((t) => t?.value !== org?.value);
      setSelectedOrganizations(updated);
      setFieldValue(
        'organizationIds',
        updated.map((m) => Number(m?.value)).filter((id) => !isNaN(id))
      );
    }
  };

  return (
    <Formik
      enableReinitialize
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={createUpdateDeviceSchema}>
      {({ errors, touched, setFieldValue, values, isValid, dirty, setFieldTouched }) => (
        <Form id='createEditForm'>
          <div
            className={clsx(
              'flex flex-col gap-y-2 sm:gap-y-10',
              selectedOrganizations?.length && '!gap-y-8'
            )}>
            <div className='grid w-full grid-cols-1 gap-x-2 sm:grid-cols-2 sm:gap-x-9'>
              {showOrganizationSelect && (
                <div className='mb-4 mt-5 md:mb-0 md:mt-9'>
                  <MultiSelect
                    placeholder={t('selectOrganizations')}
                    id='organizationIds'
                    isRequired
                    label={t('organizations')}
                    options={isLoadingOrganizations ? [] : getOrganizationOptions()}
                    handleChange={(value) => handleOrganizationChange(value, setFieldValue)}
                    fetchNextPage={fetchNextPage}
                    hasNextPage={hasNextPage}
                    selectedValues={selectedOrganizations ?? []}
                    onBlur={() => setFieldTouched('organizationIds', true)}
                    handleInputChange={(inputValue: string) => setSearchOrganization(inputValue)}
                    errorMessage={errors.organizationIds ? t('organizationRequired') : undefined}
                    isInvalid={!!errors.organizationIds && touched.organizationIds}
                    showTags
                    controlShouldRenderValue={false}
                    handleTagRemove={(org) => handleOrganizationRemove(org, setFieldValue)}
                    tagsHeight='max-h-7'
                    tagIcon={BuildingsIcon}
                  />
                </div>
              )}
              {isEditPage && (
                <div className='mb-4 mt-0 md:mb-0 md:mt-9'>
                  <SingleSelect
                    isClearable={false}
                    id='enabled'
                    label={t('availability')}
                    isRequired
                    options={isLoadingPlayerReleases ? [] : getEnabledStatusFilterOptions()}
                    handleChange={(value: SingleValue<SelectOptionProps>) =>
                      setFieldValue('enabled', value?.value)
                    }
                    value={getEnabledStatusFilterOptions().find((x) => x.value === values.enabled)}
                  />
                </div>
              )}
              <div className='mb-4 mt-0 md:mb-0 md:mt-9'>
                <SingleSelect
                  id='playerReleaseId'
                  label={t('playerReleases')}
                  options={getPlayerReleaseOptions()}
                  fetchNextPage={fetchNextPageReleases}
                  hasNextPage={hasNextPageReleases}
                  isClearable
                  handleChange={(value: SingleValue<SelectOptionProps>) =>
                    setFieldValue('playerReleaseId', value?.value)
                  }
                  value={getPlayerReleaseOptions()?.find((x) => x.value === values.playerReleaseId)}
                  onBlur={() => setFieldTouched('playerReleaseId', true)}
                />
              </div>
            </div>
            <div
              className={clsx(
                'grid w-full grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-x-9 md:gap-y-9',
                selectedOrganizations?.length && 'sm:-mt-4'
              )}>
              <Input
                type='text'
                id='name'
                label={t('name')}
                isRequired
                errorMessage={touched.name ? errors.name : undefined}
                isInvalid={!!errors.name && touched.name}
              />
              <Input
                type='text'
                id='hardwareId'
                label={t('hardwareId')}
                isRequired
                errorMessage={touched.hardwareId ? errors.hardwareId || backendError : undefined}
                isInvalid={!!errors.hardwareId && touched.hardwareId}
              />
              <Input type='text' id='streamId' label={t('streamId')} />
              <div className='col-span-1 sm:col-span-2'>
                <Textarea id='description' label={t('description')} className='h-20' />
              </div>
            </div>
          </div>
          <div className='mt-4 flex justify-end md:mt-9'>
            <Button
              type='submit'
              className='w-full md:w-fit'
              variant={ButtonVariant.PRIMARY}
              disabled={isSubmitting || !(isValid && dirty)}>
              {isEditPage ? t('saveChanges') : t('addDevice')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default CreateEditDeviceForm;
